import { cn } from '@mdxui/primitives/lib/utils'
import type { ComponentType, ReactNode } from 'react'
import { MAX_TAGS_DISPLAY } from '../lib/constants'

export interface TagFilterLinkProps {
  href: string
  children: ReactNode
  className?: string
}

export interface TagFilterProps {
  /** Available tags to filter by */
  tags: string[]
  /** Currently selected tag */
  selectedTag: string
  /** Callback when a tag is selected (callback mode) */
  onTagSelect?: (tag: string) => void
  /** Link mode — returns href for each tag (used with LinkComponent) */
  tagHref?: (tag: string) => string
  /** Link component for framework routing (e.g. Next.js Link) */
  LinkComponent?: ComponentType<TagFilterLinkProps>
  /** Optional tag counts */
  tagCounts?: Record<string, number>
  /** Maximum tags to display (default: 6) */
  maxTags?: number
  /** Additional className */
  className?: string
}

export function TagFilter({ tags, selectedTag, onTagSelect, tagHref, LinkComponent, tagCounts, maxTags = MAX_TAGS_DISPLAY, className }: TagFilterProps) {
  // Limit displayed tags to maxTags
  const displayedTags = tags.slice(0, maxTags)

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {displayedTags.map((tag) => {
        const itemClassName = cn(
          'h-8 flex items-center px-1 pl-3 rounded-[var(--radius-md,calc(var(--radius)*0.75))] cursor-pointer border border-border text-sm transition-colors',
          selectedTag === tag ? 'bg-muted text-foreground border-border' : 'border border-border bg-background hover:bg-accent',
        )

        const content = (
          <>
            <span>{tag}</span>
            {tagCounts?.[tag] && (
              <span
                className={cn(
                  'ml-2 text-xs border rounded-[var(--radius-md,calc(var(--radius)*0.75))] h-6 min-w-6 font-medium flex items-center justify-center',
                  selectedTag === tag ? 'bg-background text-foreground border-border' : 'border-border bg-muted',
                )}
              >
                {tagCounts[tag]}
              </span>
            )}
          </>
        )

        if (tagHref && LinkComponent) {
          return (
            <LinkComponent key={tag} href={tagHref(tag)} className={itemClassName}>
              {content}
            </LinkComponent>
          )
        }

        return (
          <button type='button' key={tag} onClick={() => onTagSelect?.(tag)} className={itemClassName}>
            {content}
          </button>
        )
      })}
    </div>
  )
}
